import { JSX, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PencilIcon, TrashIcon, XIcon } from 'lucide-react';
import { Box } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import { Select, type SelectValue } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { REVENOVA_ITEM_NUMBER_MAX_LENGTH } from '../commodityItemNumber';
import { weightUnitsList } from '../constants';

interface CommodityItemCardProps {
  index: number;
  formMethods: UseFormReturn<NormalizedLoad>;
  onRemove: (index: number) => void;
  fieldPath?: 'commodities';
}

const ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

export function CommodityItemCard({
  index,
  formMethods,
  onRemove,
  fieldPath = 'commodities',
}: CommodityItemCardProps): JSX.Element {
  const { control, watch } = formMethods;
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<NormalizedLoad['commodities'][0]>>>(null);

  const watchedItem = watch(`${fieldPath}.${index}`);
  const isEmptyItem = !watchedItem?.description;

  // Normalize empty strings in weight unit field when component mounts or data changes
  useEffect(() => {
    const weightUnit = formMethods.getValues(
      `${fieldPath}.${index}.weightUnit`
    );

    const normalizeValue = (value: any) => {
      if (value == null) return true;
      const strValue = String(value).trim();
      return strValue === '';
    };

    if (normalizeValue(weightUnit)) {
      formMethods.setValue(`${fieldPath}.${index}.weightUnit`, undefined, {
        shouldDirty: false,
      });
    }
  }, [index, fieldPath, formMethods]);

  // Watch weightUnit field value and normalize empty strings via Controller field
  const watchedWeightUnit = watch(`${fieldPath}.${index}.weightUnit`);
  useEffect(() => {
    if (
      watchedWeightUnit === '' ||
      (typeof watchedWeightUnit === 'string' && watchedWeightUnit.trim() === '')
    ) {
      // Normalize empty string to undefined to prevent React state update warnings
      formMethods.setValue(`${fieldPath}.${index}.weightUnit`, undefined, {
        shouldDirty: false,
      });
    }
  }, [watchedWeightUnit, index, fieldPath, formMethods]);

  // Start in editing mode if this is an empty item
  useEffect(() => {
    if (isEmptyItem && !isEditing) {
      // Capture initial state when auto-entering edit mode for new items
      // This ensures discard button works correctly for newly added items
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    }
  }, [isEmptyItem, isEditing, watchedItem]);

  // Handlers for the edit, save, discard, and remove item actions
  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      // If the original item was empty (new item), remove it entirely instead of just reverting
      const wasNewEmptyItem = !originalValues?.description;
      if (wasNewEmptyItem) {
        onRemove(index);
        return;
      }

      if (originalValues) {
        Object.keys(originalValues).forEach((key) => {
          const fieldName = `${fieldPath}.${index}.${key}` as any;
          formMethods.setValue(
            fieldName,
            originalValues[key as keyof typeof originalValues],
            {
              shouldDirty: false,
            }
          );
        });
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => {
      onRemove(index);
    },
  };

  // Render action buttons (edit, save, discard, remove)
  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1'
        >
          <ActionButton
            icon={CheckIcon}
            onClick={handlers.save}
            className='hover:bg-info-200 text-info-600'
            aria-label='Save changes'
          />
          <ActionButton
            icon={XIcon}
            onClick={handlers.discard}
            className='hover:bg-error-200 text-error-600'
            aria-label='Discard changes'
          />
        </motion.div>
      ) : (
        <motion.div
          key='viewing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1'
        >
          <ActionButton
            icon={PencilIcon}
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            aria-label='Edit item'
          />
          <ActionButton
            icon={TrashIcon}
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            aria-label='Remove item'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Handle Enter key contextually
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // If hitting enter in the editing form, save changes and collapse form
      if (isEditing) {
        e.preventDefault();
        e.stopPropagation();
        handlers.save();
      } else {
        return;
      }
    }
  };

  // Render the editing form for the commodity input card
  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden'
    >
      {/* Top header with item number and actions */}
      <Flex justify='between' align='center' className='mb-2'>
        <Flex align='center' gap='sm'>
          <Box className='h-6 w-6' />
          <Typography
            variant='caption'
            className='text-neutral-600 font-semibold'
          >
            {index + 1}
          </Typography>
        </Flex>

        {/* Action buttons */}
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      {/* Name field */}
      <div className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.description`}
          label='Name'
          required={true}
          options={{
            required: 'Item name is required',
          }}
        />
      </div>

      {/* Description field */}
      <div className='mx-0 w-full mb-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.additionalMarkings`}
          label='Description'
          required={false}
        />
      </div>

      {/* Item counts and handling units are independent, e.g. 500 cases on 10 pallets. */}
      <Grid cols='2' gap='sm' className='w-full mb-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.quantity`}
          label='Quantity'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <RHFTextInput
          name={`${fieldPath}.${index}.packagingType`}
          label='Quantity units'
          placeholder='e.g. EA, Cases'
          required={false}
        />
        <RHFTextInput
          name={`${fieldPath}.${index}.handlingQuantity`}
          label='Handling quantity'
          inputType='number'
          step={1}
          options={{
            valueAsNumber: true,
            min: 0,
            validate: (value) =>
              value == null ||
              Number.isNaN(value) ||
              Number.isInteger(value) ||
              'Enter a whole number',
          }}
          required={false}
        />
        <RHFTextInput
          name={`${fieldPath}.${index}.handlingUnitTypeName`}
          label='Handling units'
          placeholder='e.g. Pallets, Skids'
          required={false}
        />
      </Grid>

      {/* Weight and Weight Units */}
      <Grid cols='2' gap='sm' className='mx-0 w-full'>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.weightTotal`}
            label='Weight'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>

        <div className='col-span-1'>
          <Label name={`${fieldPath}.${index}.weightUnit`} required={false}>
            Weight Units
          </Label>
          <Controller
            name={`${fieldPath}.${index}.weightUnit`}
            control={control}
            defaultValue={undefined}
            render={({ field }) => {
              // Filter out empty strings, null, and undefined from units list
              const validUnits = (weightUnitsList || [])
                .filter((unit) => {
                  const str = String(unit);
                  const isValid =
                    str != null &&
                    str !== '' &&
                    str.trim() !== '' &&
                    str.length > 0;
                  return isValid;
                })
                .map((unit) => String(unit).trim());

              // Ensure value is never empty string - convert to undefined immediately
              const rawValue = field.value;
              let safeValue: Undef<string> = undefined;

              // Convert to string and check - be very strict
              if (rawValue != null && rawValue !== '') {
                const stringValue = String(rawValue).trim();
                // Only set safeValue if it's a non-empty string AND exists in validUnits
                if (
                  stringValue !== '' &&
                  stringValue.length > 0 &&
                  validUnits.includes(stringValue)
                ) {
                  safeValue = stringValue;
                }
              }

              // Final safety check: ensure safeValue is never empty string or null
              if (!safeValue || safeValue === '' || safeValue.trim() === '') {
                safeValue = undefined;
              }

              // Normalize onChange to prevent empty strings - SelectValue can be MaybeUndef<string | number>
              const handleChange = (value: SelectValue) => {
                if (value == null || value === '') {
                  field.onChange(undefined);
                } else {
                  const stringValue = String(value).trim();
                  if (stringValue === '') {
                    field.onChange(undefined);
                  } else {
                    field.onChange(stringValue);
                  }
                }
              };

              // Ensure options never have empty string values - filter and validate
              // This is critical: SelectItem cannot have empty string values
              const safeOptions = validUnits
                .filter((unit) => {
                  const str = String(unit);
                  const isValid =
                    str != null &&
                    str !== '' &&
                    str.trim() !== '' &&
                    str.length > 0;
                  return isValid;
                })
                .map((unit) => {
                  const str = String(unit).trim();
                  // Final safety check - never allow empty string or whitespace-only
                  if (str === '' || str.length === 0) {
                    return null;
                  }
                  return {
                    value: str,
                    label: str,
                  };
                })
                .filter((opt): opt is { value: string; label: string } => {
                  // Triple-check: ensure value is never empty string
                  if (
                    opt == null ||
                    opt.value === '' ||
                    opt.value.trim() === '' ||
                    opt.value.length === 0
                  ) {
                    return false;
                  }
                  return true;
                });

              // Final guard: never pass empty string to Select - must be undefined or valid string
              const finalValue =
                safeValue &&
                safeValue !== '' &&
                safeValue.trim() !== '' &&
                safeValue.length > 0
                  ? safeValue
                  : undefined;

              return (
                <Select
                  showSearch
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={handleChange}
                  value={finalValue}
                  options={safeOptions}
                />
              );
            }}
          />
        </div>
      </Grid>
    </motion.div>
  );

  // Render the collapsed view (read-only display)
  const renderCollapsedView = () => {
    const name = watchedItem?.description || 'Unnamed item';
    const description = watchedItem?.additionalMarkings || '';
    const quantity = watchedItem?.quantity ?? 0;
    const handlingQuantity = watchedItem?.handlingQuantity;
    const weight = watchedItem?.weightTotal ?? 0;
    const weightUnit = watchedItem?.weightUnit || '';

    return (
      <motion.div
        key='collapsed-view'
        {...ANIMATIONS.toggle}
        className='overflow-hidden'
      >
        <Flex justify='between' align='center' className='py-2'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0'>
            <Box className='h-6 w-6 flex-shrink-0' />
            <Typography
              variant='caption'
              className='text-neutral-600 font-semibold flex-shrink-0'
            >
              {index + 1}
            </Typography>
            <div className='flex-1 min-w-0'>
              <Typography
                variant='body-sm'
                className='text-neutral-900 font-medium truncate'
              >
                {name}
              </Typography>
              {description && (
                <Typography
                  variant='body-xs'
                  className='text-neutral-600 truncate'
                >
                  {description}
                </Typography>
              )}
              <Flex align='center' gap='sm' className='mt-1'>
                {handlingQuantity != null &&
                  handlingQuantity > 0 &&
                  Number.isFinite(handlingQuantity) && (
                    <Typography variant='body-xs' textColor='muted'>
                      Handling: {handlingQuantity}{' '}
                      {watchedItem?.handlingUnitTypeName}
                    </Typography>
                  )}
                {quantity > 0 && (
                  <Typography variant='body-xs' className='text-neutral-500'>
                    Qty: {quantity} {watchedItem?.packagingType}
                  </Typography>
                )}
                {weight > 0 && (
                  <Typography variant='body-xs' className='text-neutral-500'>
                    Weight: {weight} {weightUnit}
                  </Typography>
                )}
              </Flex>
            </div>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
        </Flex>
      </motion.div>
    );
  };

  return (
    <motion.div
      {...ANIMATIONS.card}
      className={cn(
        'rounded-lg border border-neutral-200 bg-neutral-50 p-3 transition-colors',
        isEditing && 'border-brand-main bg-neutral-50'
      )}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderCollapsedView()}
      </AnimatePresence>
      {(watchedItem?.description?.length || 0) >
        REVENOVA_ITEM_NUMBER_MAX_LENGTH && (
        <Typography
          role='status'
          variant='body-sm'
          className='mt-2 text-warning-700'
        >
          Name exceeds {REVENOVA_ITEM_NUMBER_MAX_LENGTH} characters. When
          submitted, it will be shortened and the full text will be preserved in
          Description.
        </Typography>
      )}
    </motion.div>
  );
}
